import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Send, Loader2, CheckCircle2, MessageSquare, User, AtSign, Type, ChevronDown } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Contact — iteration 117.
 *
 * Public + authenticated contact form. The raw support email is
 * intentionally hidden from anonymous visitors (public site is the
 * primary spam vector — scrapers pull `mailto:` links straight off
 * the DOM). Authenticated parents still see a fallback line so they
 * can use their own email client if they prefer.
 */
const TOPICS = [
  { slug: "order_issue",      label: "Order Issue" },
  { slug: "banking_wallet",   label: "Banking & Wallet" },
  { slug: "general_feedback", label: "General Feedback" },
  { slug: "account_query",    label: "Account Query" },
  // Iteration 302 — catch-all topic (backend allow-list kept in sync).
  { slug: "other",            label: "Other / Something Else" },
];

export default function Contact() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const authed = Boolean(user && user.id);
  // Deep-link `?topic=<slug>` from Info Hub / Info topic CTAs pre-
  // selects the dropdown so folks landing here from a specific info
  // topic don't have to re-choose.
  const initialTopic = (() => {
    const q = (searchParams.get("topic") || "").toLowerCase();
    return TOPICS.some((t) => t.slug === q) ? q : TOPICS[0].slug;
  })();
  const [form, setForm] = useState({
    topic: initialTopic,
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [ticketRef, setTicketRef] = useState("");
  const [err, setErr] = useState("");

  // Pre-fill Name + Email when a parent is signed in.
  useEffect(() => {
    if (authed && (user.name || user.email)) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
      }));
    }
  }, [authed, user]);

  // Iteration 302b — arriving via a "Contact Us" link keeps the previous
  // page's scroll offset, so mobile/tablet users land stuck at the bottom
  // of the form. Smooth-scroll to the form top ("We're here to help.") on
  // mount — with retries: on iPad/tablet the first attempt can be undone
  // by late font/image layout shifts, so we re-assert at 600ms + 1400ms
  // (no-ops when already in position).
  useEffect(() => {
    const go = () => {
      document.getElementById("contact-heading")?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const timers = [150, 600, 1400].map((ms) => setTimeout(go, ms));
    return () => timers.forEach(clearTimeout);
  }, []);

  const canSubmit = useMemo(() => {
    return form.topic
      && form.name.trim().length >= 1
      && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
      && form.subject.trim().length >= 1
      && form.message.trim().length >= 8
      && status !== "sending";
  }, [form, status]);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus("sending"); setErr("");
    try {
      const { data } = await api.post("/support/contact", {
        topic: form.topic,
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      setTicketRef(data?.ticket_ref || "");
      setStatus("sent");
      toast.success(data?.message || "Thanks — we've got your message! Expect a reply within 24 hours.");
      // Clear the message field so parents can send follow-ups without
      // re-typing name / email / subject.
      setForm((f) => ({ ...f, subject: "", message: "" }));
    } catch (ex) {
      setErr(formatApiError(ex) || "Something went wrong — please try again in a minute.");
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Navbar />
      <div className="max-w-[560px] md:max-w-[640px] xl:max-w-3xl mx-auto px-4 md:px-6 pt-10 pb-28 md:py-16" data-testid="contact-page">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brandTeal">
            <MessageSquare className="w-4 h-4" /> Contact us
          </div>
          <h1 id="contact-heading" className="mt-3 font-display text-4xl md:text-5xl font-medium text-slate-900 leading-tight scroll-mt-20" data-testid="contact-heading">
            We&apos;re here to help.
          </h1>
          <p className="mt-3 text-slate-600 text-base md:text-lg">
            Pick a topic below and drop us a note — we reply within 24 hours.
          </p>
        </div>

        {status === "sent" && ticketRef && (
          <div className="card-base p-5 mb-6 bg-emerald-50 border-emerald-200 flex items-start gap-3" data-testid="contact-success">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-display text-lg font-semibold text-emerald-900">Message received</div>
              <div className="text-sm text-emerald-800 mt-0.5">
                Ticket <span className="font-mono font-bold" data-testid="contact-ticket-ref">{ticketRef}</span> — we&apos;ll email you back within 24 hours.
              </div>
            </div>
          </div>
        )}

        <form onSubmit={submit} className="card-base p-6 md:p-8 space-y-5" data-testid="contact-form">
          {/* Topic */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-1.5" htmlFor="contact-topic">Topic</label>
            <div className="relative">
              <select
                id="contact-topic"
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                className="w-full appearance-none rounded-2xl border border-slate-300 focus:border-brandTeal focus:ring-2 focus:ring-brandTeal/20 outline-none bg-white pr-10 pl-4 py-3 min-h-[44px] text-sm text-slate-900 font-semibold"
                data-testid="contact-topic-select"
                required
              >
                {TOPICS.map((t) => (
                  <option key={t.slug} value={t.slug}>{t.label}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-slate-500 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          {/* Name + Email */}
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1.5" htmlFor="contact-name">Your name</label>
              <div className="relative">
                <User className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  id="contact-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-2xl border border-slate-300 focus:border-brandTeal focus:ring-2 focus:ring-brandTeal/20 outline-none bg-white pl-10 pr-4 py-3 text-sm text-slate-900"
                  data-testid="contact-name-input"
                  placeholder="Alex Chen"
                  autoComplete="name"
                  required
                  maxLength={120}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1.5" htmlFor="contact-email">Email</label>
              <div className="relative">
                <AtSign className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  id="contact-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-2xl border border-slate-300 focus:border-brandTeal focus:ring-2 focus:ring-brandTeal/20 outline-none bg-white pl-10 pr-4 py-3 text-sm text-slate-900"
                  data-testid="contact-email-input"
                  placeholder="you@example.co.uk"
                  autoComplete="email"
                  required
                />
              </div>
              {authed && (
                <div className="mt-1.5 text-[11px] text-emerald-700 font-semibold inline-flex items-center gap-1" data-testid="contact-prefill-note">
                  <CheckCircle2 className="w-3 h-3" /> Pre-filled from your account
                </div>
              )}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-1.5" htmlFor="contact-subject">Subject</label>
            <div className="relative">
              <Type className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                id="contact-subject"
                type="text"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="w-full rounded-2xl border border-slate-300 focus:border-brandTeal focus:ring-2 focus:ring-brandTeal/20 outline-none bg-white pl-10 pr-4 py-3 text-sm text-slate-900"
                data-testid="contact-subject-input"
                placeholder="Order #TT-1234 didn't arrive"
                required
                maxLength={200}
              />
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-1.5" htmlFor="contact-message">Message</label>
            <textarea
              id="contact-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full rounded-2xl border border-slate-300 focus:border-brandTeal focus:ring-2 focus:ring-brandTeal/20 outline-none bg-white px-4 py-3 text-sm text-slate-900 min-h-[140px]"
              placeholder="Tell us what's happening — the more detail the faster we can help."
              rows={6}
              required
              minLength={8}
              maxLength={5000}
              data-testid="contact-message-input"
            />
            <div className="mt-1.5 text-[11px] text-slate-500 text-right">{form.message.length}/5000</div>
          </div>

          {err && (
            <div className="rounded-2xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-800" data-testid="contact-error">{err}</div>
          )}

          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] text-slate-500">
              By submitting you agree to our <Link to="/info/privacy" className="underline hover:text-slate-700">privacy policy</Link>. We only email you back about your query.
            </p>
            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex items-center gap-2 px-5 py-3 min-h-[44px] rounded-full bg-brandTeal hover:bg-brandTeal/90 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold text-sm shadow-md transition"
              data-testid="contact-submit-btn"
            >
              {status === "sending" ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
              ) : (
                <><Send className="w-4 h-4" /> Send message</>
              )}
            </button>
          </div>
        </form>

        {/* Authenticated fallback — email address is only surfaced to
            signed-in parents. Public visitors NEVER see it, defeating
            bulk-scrapers hitting the marketing site. */}
        {authed && (
          <div className="mt-6 card-base p-5 bg-slate-50 border-slate-200 flex items-start gap-3" data-testid="contact-authenticated-fallback">
            <Mail className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-700">
              <span className="font-semibold text-slate-900">Prefer using your own email client?</span>{" "}
              Write to us directly at{" "}
              <a
                href="mailto:hello@mytabletopapp.co.uk"
                className="font-semibold text-brandTeal hover:underline"
                data-testid="contact-direct-email"
              >
                hello@mytabletopapp.co.uk
              </a>
              .
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
